import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CalendarIcon, AlertTriangle } from "lucide-react";
import { format, addYears } from "date-fns";
import { ko } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Plan, SubscriptionCycle, SubscriptionStatus, Platform } from "@/types/enums";
import type { AdminSubscriptionItem } from "@/types/subscription";
import { useUpgradeSubscription } from "@/hooks/use-subscriptions";
import { PlanBadge } from "./plan-badge";

const GRANTABLE_PLANS = [Plan.PRO_TRIAL, Plan.STUDENT_PASS, Plan.PLUS, Plan.PREMIUM] as const;

type GrantablePlan = (typeof GRANTABLE_PLANS)[number];

const PLAN_LABEL: Record<GrantablePlan, string> = {
  [Plan.PRO_TRIAL]: "PRO 체험판 (PRO_TRIAL)",
  [Plan.STUDENT_PASS]: "학생 패스 (STUDENT_PASS)",
  [Plan.PLUS]: "PLUS (유료)",
  [Plan.PREMIUM]: "PREMIUM (유료)",
};

const PLAN_DESCRIPTION: Record<GrantablePlan, string> = {
  [Plan.PRO_TRIAL]: "지정된 일수 동안 PRO 기능을 사용할 수 있습니다.",
  [Plan.STUDENT_PASS]: "지정된 종료일까지 PRO 기능을 사용할 수 있습니다.",
  [Plan.PLUS]: "토스 결제 구독으로 시작합니다.",
  [Plan.PREMIUM]: "토스 결제 구독으로 시작합니다.",
};

function isGrantablePlan(value: string): value is GrantablePlan {
  return (GRANTABLE_PLANS as readonly string[]).includes(value);
}

function isPaidGrant(plan: GrantablePlan): boolean {
  return plan === Plan.PLUS || plan === Plan.PREMIUM;
}

function isSubscriptionCycle(value: string): value is SubscriptionCycle {
  return value === SubscriptionCycle.MONTHLY || value === SubscriptionCycle.YEARLY;
}

const upgradeSchema = z
  .object({
    targetPlan: z.union([
      z.literal(Plan.PRO_TRIAL),
      z.literal(Plan.STUDENT_PASS),
      z.literal(Plan.PLUS),
      z.literal(Plan.PREMIUM),
    ]),
    subscriptionCycle: z
      .union([z.literal(SubscriptionCycle.MONTHLY), z.literal(SubscriptionCycle.YEARLY)])
      .optional(),
    durationDays: z.number().int().min(1).max(365).optional(),
    endDate: z.date().optional(),
    reason: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.targetPlan === Plan.PRO_TRIAL && (!data.durationDays || data.durationDays <= 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "PRO_TRIAL은 기간(일)을 입력해야 합니다",
        path: ["durationDays"],
      });
    }
    if (data.targetPlan === Plan.STUDENT_PASS && !data.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "STUDENT_PASS는 종료일을 선택해야 합니다",
        path: ["endDate"],
      });
    }
    if (isPaidGrant(data.targetPlan) && !data.subscriptionCycle) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "결제 주기를 선택해야 합니다",
        path: ["subscriptionCycle"],
      });
    }
  });

type UpgradeFormValues = z.infer<typeof upgradeSchema>;

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.ACTIVE]: "활성",
  [SubscriptionStatus.PENDING_PAYMENT]: "결제 대기",
  [SubscriptionStatus.PENDING_CANCELLATION]: "해지 대기",
  [SubscriptionStatus.PENDING_PLAN_CHANGE]: "플랜 변경 대기",
};

function canUpgrade(subscription: AdminSubscriptionItem): {
  allowed: boolean;
  reason?: string;
} {
  if (subscription.platform === Platform.IOS) {
    return {
      allowed: false,
      reason: "IOS 사용자는 App Store에서 구독을 관리해야 합니다.",
    };
  }
  if (subscription.platform === Platform.ANDROID) {
    return {
      allowed: false,
      reason: "Android 사용자는 Play Store에서 구독을 관리해야 합니다.",
    };
  }
  if (subscription.platform === Platform.STRIPE) {
    return {
      allowed: false,
      reason: "STRIPE 사용자는 Stripe 대시보드에서 구독을 관리해야 합니다.",
    };
  }

  if (
    (subscription.plan === Plan.PRO ||
      subscription.plan === Plan.PLUS ||
      subscription.plan === Plan.PREMIUM) &&
    subscription.price > 0
  ) {
    return {
      allowed: false,
      reason: `유료 ${subscription.plan} 사용자는 결제 플랫폼에서 구독을 관리해야 합니다.`,
    };
  }

  if (subscription.status === SubscriptionStatus.PENDING_PAYMENT) {
    return {
      allowed: false,
      reason: `현재 구독 상태가 "${
        STATUS_LABEL[subscription.status]
      }"입니다. 활성 상태의 구독만 업그레이드할 수 있습니다.`,
    };
  }
  if (subscription.status === SubscriptionStatus.PENDING_PLAN_CHANGE) {
    return {
      allowed: false,
      reason: `현재 구독 상태가 "${STATUS_LABEL[subscription.status]}"입니다. 기존 변경을 취소한 후 진행해주세요.`,
    };
  }

  if (subscription.plan === Plan.PRO_TRIAL || subscription.plan === Plan.STUDENT_PASS) {
    return {
      allowed: false,
      reason: "이 플랜은 '연장' 기능을 사용해주세요.",
    };
  }

  return { allowed: true };
}

interface UpgradeDialogProps {
  subscription: AdminSubscriptionItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpgradeDialog({ subscription, open, onOpenChange }: UpgradeDialogProps) {
  const { mutate: upgrade, isPending } = useUpgradeSubscription();
  const upgradeCheck = canUpgrade(subscription);

  const form = useForm<UpgradeFormValues>({
    resolver: zodResolver(upgradeSchema),
    defaultValues: {
      targetPlan: Plan.PRO_TRIAL,
      subscriptionCycle: undefined,
      durationDays: 30,
      endDate: undefined,
      reason: "",
    },
  });

  const selectedPlan = form.watch("targetPlan");
  const isPaidSelected = isPaidGrant(selectedPlan);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) form.reset();
    onOpenChange(isOpen);
  };

  const onSubmit = (values: UpgradeFormValues) => {
    upgrade(
      {
        userId: subscription.userId,
        plan: values.targetPlan,
        subscriptionCycle: isPaidGrant(values.targetPlan) ? values.subscriptionCycle : undefined,
        durationDays: values.targetPlan === Plan.PRO_TRIAL ? values.durationDays : undefined,
        endDate: values.targetPlan === Plan.STUDENT_PASS ? values.endDate?.toISOString() : undefined,
        reason: values.reason,
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>구독 업그레이드</DialogTitle>
          <DialogDescription>
            사용자의 구독을 체험 플랜(PRO_TRIAL / STUDENT_PASS) 또는 유료 플랜(PLUS / PREMIUM)으로
            변경합니다.
          </DialogDescription>
        </DialogHeader>

        {!upgradeCheck.allowed ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>업그레이드 불가</AlertTitle>
            <AlertDescription>{upgradeCheck.reason}</AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">사용자</span>
                <span className="text-sm">{subscription.userName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">현재 플랜</span>
                <PlanBadge plan={subscription.plan} />
              </div>
            </div>

            {subscription.status === SubscriptionStatus.PENDING_CANCELLATION && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>해지 대기 중인 구독입니다. 업그레이드하면 해지가 취소됩니다.</AlertDescription>
              </Alert>
            )}

            {isPaidSelected && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>실제 결제가 발생합니다</AlertTitle>
                <AlertDescription>
                  <ul className="ml-4 list-disc space-y-1 text-sm">
                    <li>플랫폼이 TOSS로 설정되어 다음 결제일에 저장된 빌링키로 정가가 청구됩니다</li>
                    <li>
                      등록된 카드가 없으면 결제가 조용히 실패한 채 스케줄러가 재시도만 반복합니다
                      (크레딧 미지급, 알림 없음) — 카드 등록 여부를 먼저 확인하세요
                    </li>
                    <li>월간 크레딧이 해당 플랜 지급량으로 초기화됩니다</li>
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="targetPlan"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>업그레이드 플랜 *</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          if (isGrantablePlan(value)) {
                            field.onChange(value);
                          }
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="플랜 선택" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {GRANTABLE_PLANS.map((plan) => (
                            <SelectItem key={plan} value={plan}>
                              {PLAN_LABEL[plan]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>{PLAN_DESCRIPTION[selectedPlan]}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {isPaidSelected && (
                  <FormField
                    control={form.control}
                    name="subscriptionCycle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>결제 주기 *</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            if (isSubscriptionCycle(value)) {
                              field.onChange(value);
                            }
                          }}
                          value={field.value ?? ""}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="주기 선택" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={SubscriptionCycle.MONTHLY}>월간</SelectItem>
                            <SelectItem value={SubscriptionCycle.YEARLY}>연간</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          월간은 1개월 뒤, 연간은 1년 뒤가 다음 결제일이 됩니다.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {selectedPlan === Plan.PRO_TRIAL && (
                  <FormField
                    control={form.control}
                    name="durationDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>기간 (일) *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            max={365}
                            {...field}
                            onChange={(e) => {
                              const parsed = parseInt(e.target.value, 10);
                              field.onChange(Number.isNaN(parsed) ? undefined : parsed);
                            }}
                          />
                        </FormControl>
                        <FormDescription>1일 ~ 365일</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {selectedPlan === Plan.STUDENT_PASS && (
                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>종료일 *</FormLabel>
                        <div className="flex flex-wrap gap-2">
                          {[1, 2, 3, 4].map((years) => (
                            <Button
                              key={years}
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => field.onChange(addYears(new Date(), years))}
                            >
                              +{years}년
                            </Button>
                          ))}
                        </div>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full pl-3 text-left font-normal",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                {field.value ? format(field.value, "PPP", { locale: ko }) : <span>날짜 선택</span>}
                                <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              captionLayout="dropdown"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date < new Date()}
                              startMonth={new Date()}
                              endMonth={addYears(new Date(), 10)}
                              locale={ko}
                            />
                          </PopoverContent>
                        </Popover>
                        <FormDescription>학생 패스의 종료일을 선택합니다.</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>사유 (선택)</FormLabel>
                      <FormControl>
                        <Input placeholder="업그레이드 사유를 입력하세요..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
            취소
          </Button>
          {upgradeCheck.allowed && (
            <Button
              type="button"
              onClick={form.handleSubmit(onSubmit)}
              disabled={isPending}
              aria-label="Confirm upgrade"
            >
              {isPending ? "처리 중..." : "업그레이드 확인"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
